import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { catchError, concatMap, finalize, from, Observable, of, throwError, toArray } from 'rxjs';
import { ToastService } from '../../core/services/toast';
import { AuthService } from '../../core/services/auth';
import { environment } from '../../../environments/environment';
import { DevProdSyncModalStateService } from '../../core/services/dev-prod-sync-modal';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './header.html',
  styleUrl: './header.css'
})
export class HeaderComponent {
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastService);
  private readonly auth = inject(AuthService);
  private readonly devProdSyncModal = inject(DevProdSyncModalStateService);

  currentDate = new Date();
  isTriggeringWorkers = signal(false);
  isTriggeringDietSync = signal(false);
  isTriggeringDermoscanSync = signal(false);
  canUseDevProdSync = computed(() => {
    const roles = this.auth.currentUser()?.roles as string[] | undefined;
    return environment.devProdSyncButtonEnabled && (roles?.includes('super_admin') ?? false);
  });

  openDevProdSyncModal(): void {
    if (!this.canUseDevProdSync()) return;
    this.devProdSyncModal.open();
  }

  triggerWorkers(): void {
    if (this.isTriggeringWorkers()) return;

    this.isTriggeringWorkers.set(true);

    this.triggerCycleJobs()
      .pipe(
        catchError((err: unknown) => {
          console.error('[Header] cycle trigger jobs failed:', err);
          const message = typeof err === 'string' ? err : JSON.stringify(err);
          this.toast.show(`Cycle jobs failed: ${message}`, 'error', 6000);
          return of(null);
        }),
        concatMap(() =>
          this.triggerYogaContentSyncJob().pipe(
            catchError((err: unknown) => {
              console.error('[Header] triggerYogaContentSyncJob failed:', err);
              const message = typeof err === 'string' ? err : JSON.stringify(err);
              this.toast.show(`Yoga sync failed: ${message}`, 'error', 6000);
              return of(null);
            }),
          ),
        ),
      )
      .pipe(finalize(() => this.isTriggeringWorkers.set(false)))
      .subscribe({
        next: () => {
          this.toast.show('Cycle jobs and yoga sync were triggered.', 'success');
        },
        error: (err: unknown) => {
          console.error('[Header] triggerWorkers failed:', err);
          const message = typeof err === 'string' ? err : JSON.stringify(err);
          this.toast.show(message, 'error', 6000);
        },
      });
  }

  private triggerCycleJobs(): Observable<unknown[]> {
    const hostname = window.location.hostname;
    const isLocalhost =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '::1';
    const url =
      isLocalhost || hostname === 'discont.amatis.dev'
        ? 'https://cycle.amatis.dev/api/v1/cycles/triggerjob'
        : hostname === 'discont.amatis.work'
          ? 'https://api.evacycle.app/api/v1/cycles/triggerjob'
          : null;

    if (!url) {
      console.warn('[Header] cycle trigger jobs skipped for hostname:', hostname);
      return of([]);
    }

    return from([1, 2, 3]).pipe(
      concatMap((jobType) => this.http.post(url, { jobType })),
      toArray(),
    );
  }

  private triggerYogaContentSyncJob(): Observable<unknown> {
    const accessToken = this.auth.getAccessToken();
    if (!accessToken) {
      return throwError(() => new Error('No access token found.'));
    }

    return this.http.post(
      `${environment.apiUrl}/agents/yoga/triggerjob`,
      {},
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
  }

  triggerDietSync(): void {
    if (this.isTriggeringDietSync()) return;

    const accessToken = this.auth.getAccessToken();
    if (!accessToken) {
      this.toast.show('No access token found.', 'error', 6000);
      return;
    }

    this.isTriggeringDietSync.set(true);

    this.http.post(
      `${environment.apiUrl}/agents/diet/triggerjob`,
      {},
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    )
      .pipe(finalize(() => this.isTriggeringDietSync.set(false)))
      .subscribe({
        next: () => {
          this.toast.show('DIET content sync completed.', 'success');
        },
        error: (err: unknown) => {
          console.error('[Header] triggerDietSync failed:', err);
          const message = typeof err === 'string' ? err : JSON.stringify(err);
          this.toast.show(`DIET sync failed: ${message}`, 'error', 6000);
        },
      });
  }

  triggerDermoscanSync(): void {
    if (this.isTriggeringDermoscanSync()) return;

    const accessToken = this.auth.getAccessToken();
    if (!accessToken) {
      this.toast.show('No access token found.', 'error', 6000);
      return;
    }

    this.isTriggeringDermoscanSync.set(true);

    this.http.post(
      `${environment.apiUrl}/agents/dermoscan/triggerjob`,
      {},
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    )
      .pipe(finalize(() => this.isTriggeringDermoscanSync.set(false)))
      .subscribe({
        next: () => {
          this.toast.show('DermoScan content sync completed.', 'success');
        },
        error: (err: unknown) => {
          console.error('[Header] triggerDermoscanSync failed:', err);
          const message = typeof err === 'string' ? err : JSON.stringify(err);
          this.toast.show(`DermoScan sync failed: ${message}`, 'error', 6000);
        },
      });
  }
}
